import type { Router, NavigationExtras } from '@angular/router';
import type { Form, FormButton } from '../../interfaces/form.interface';

export interface ReactiveFormNavigationFacadeConfiguration<T> {
  readonly getForm: () => Form<T> | null;
  readonly router: Router;
  readonly emitGoBack: () => void;
  readonly emitHideDialog: () => void;
  readonly openWindow?: (href: string, target: string, features?: string) => void;
}

/**
 * Owns navigation and header-button execution for the reactive form shell.
 *
 * The builder component exposes template-facing methods, while this facade keeps callback, router and external-link
 * behavior in one testable place.
 */
export class ReactiveFormNavigationFacade<T> {
  constructor(private readonly configuration: ReactiveFormNavigationFacadeConfiguration<T>) {}

  public returnToMainView(): void {
    this.configuration.emitGoBack();
  }

  public closeDialog(): void {
    this.configuration.emitHideDialog();
  }

  public getHeaderButtons(): FormButton[] {
    const form = this.configuration.getForm();
    const rootButtons = this.normalizeButtons(form?.button);
    const explicitButtons = this.normalizeButtons(form?.buttons);

    return [...rootButtons, ...explicitButtons].filter((button) => button.isVisible !== false);
  }

  public executeConfiguredButton(button: FormButton): void {
    if (button.type === 'callback') {
      button.function?.();
      return;
    }

    if (button.type === 'externalLink') {
      this.openExternalLink(button);
      return;
    }

    if (button.type === 'routerLink') {
      this.navigateToRoute(button);
    }
  }

  private normalizeButtons(buttonOrButtons: FormButton | FormButton[] | undefined): FormButton[] {
    if (Array.isArray(buttonOrButtons)) {
      return buttonOrButtons;
    }

    return buttonOrButtons ? [buttonOrButtons] : [];
  }

  private openExternalLink(button: FormButton): void {
    const href = typeof button.href === 'string' ? button.href.trim() : '';

    if (href === '') {
      return;
    }

    const target = button.target ?? '_self';
    const features = target === '_blank' ? (button.rel ?? 'noopener,noreferrer') : undefined;
    const openWindow = this.configuration.openWindow ?? this.defaultOpenWindow;

    openWindow(href, target, features);
  }

  private navigateToRoute(button: FormButton): void {
    if (!Array.isArray(button.route) || button.route.length === 0) {
      return;
    }

    const navigationExtras: NavigationExtras = {};

    if (button.queryParams !== undefined) {
      navigationExtras.queryParams = button.queryParams;
    }

    if (typeof button.fragment === 'string' && button.fragment.trim() !== '') {
      navigationExtras.fragment = button.fragment;
    }

    void this.configuration.router.navigate(button.route, navigationExtras);
  }

  private defaultOpenWindow(href: string, target: string, features?: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.open(href, target, features);
  }
}
